import { ProductQuantitySubmitFormData } from '../../../containers/@forms/CartItemForm/validations';
import {
    CheckoutStateResource,
    CreateOrderItemResponse,
    CreateOrderResponse,
    defaultSyliusImageType,
    DeleteOrderItemResponse,
    DeleteOrderResponse,
    DuplicateOrderResponse,
    GetOrderPricesResponse,
    GetOrderResponse,
    GetProductResponse,
    GetProductVariantResponse,
    OrderResource,
    PaymentStateResource,
    PricesResource,
    ProductAttributeKey,
    SetOrderItemResponse,
    syliusCustomerFetch,
    syliusFetch,
} from '../../../entities/@api/Sylius';
import { transformToProductCode } from '../../../entities/@products/Product/ProductTransformers';
import { transformToProductAttribute } from '../../../entities/@products/ProductAttribute/ProductAttributeTransformers';
import { locale } from '../../../entities/@products/ProductVariant/ProductVariantTransformers';
import { CartInterface, CartItem, cartToken } from '../../../entities/@shop/Cart/Cart';
import { transformToCart } from '../../../entities/@shop/Cart/CartTransformers';
import { fetchTaxonHierarchy } from '../../../entities/@shop/Taxon/TaxonService';
import { transformBrandNameToLink } from '../../../entities/Brand/BrandTransformers';
import { FetchResultSyliusErrorType, FetchResultType, isFetchResultSuccessful } from '../../../entities/FetchResult/FetchResult';
import { defaultImage, transformImagePathToImage } from '../../../entities/Media/MediaTransformers';
import { ReduxAction, ReduxFetchAction } from '../../defaults';
import { setOrder } from '../order/orderSlice';
import {
    defaultCart,
    setCart,
    setError,
    setHasFetched,
    setIsInitiallyLoading,
    setIsLoading,
    setIsSuccessfullyAdded,
    setIsSuccessfullyRemoved,
} from './cartSlice';

interface GetCartByTokenConfig {
    storedCartToken: string;
    skipPriceFetch?: boolean;
}

interface FetchCartConfig {
    skipPriceFetch?: boolean;
}

const fetchCartItem = async (cartItem: CartItem): Promise<CartItem> => {
    const { variantCode } = cartItem;
    const productVariantResponse = await syliusFetch<GetProductVariantResponse>(`/shop/product-variants/${variantCode}`);

    if (!isFetchResultSuccessful(productVariantResponse)) {
        console.error('[fetchCartItem]', productVariantResponse.error);
        return cartItem;
    }

    const {
        id,
        product,
        onHand: stock,
        attributes,
        images,
        loods5IdLegacy,
        name,
    } = productVariantResponse.data;

    const variantId = id?.toString() || '';

    const translatedProductAttributes = attributes
        ? attributes.filter(attribute => attribute.localeCode === locale)
        : [];

    const productVariantAttributes = translatedProductAttributes
        ? translatedProductAttributes.map(transformToProductAttribute)
        : [];

    const productVariantAttributesWithData = productVariantAttributes.filter(productVariantAttribute => productVariantAttribute.value);

    const brandAttribute = productVariantAttributesWithData.find(productAttribute => productAttribute.key === ProductAttributeKey.brand);
    const brand = brandAttribute
        ? transformBrandNameToLink(brandAttribute.value)
        : undefined;

    const productCode = transformToProductCode(product);

    const imageFilterQuery = `?imageFilter=${defaultSyliusImageType}`;
    const productResponse = await syliusFetch<GetProductResponse>(`/shop/products/${productCode}${imageFilterQuery}`);

    if (!isFetchResultSuccessful(productResponse)) {
        console.error('[fetchCartItem]', productResponse.error);
        return cartItem;
    }

    const productDefaultResource = productResponse.data;

    const taxonHierarchy = await fetchTaxonHierarchy(productDefaultResource.mainTaxon || '');

    const parentCategories = taxonHierarchy
        ? taxonHierarchy.parents.map(parent => parent.name)
        : [];

    const categories = taxonHierarchy
        ? [...parentCategories, taxonHierarchy.name]
        : parentCategories;

    const productDefaultImages = productDefaultResource.images;

    const variantImage = productDefaultImages.find(productImage => images.includes(productImage['@id']));

    const image = variantImage?.path
        ? transformImagePathToImage(variantImage.path, name)
        : defaultImage();

    return {
        ...cartItem,
        variantId,
        variantCode,
        productCode,
        legacyId: loods5IdLegacy?.toString(),
        stock,
        categories,
        brand,
        image,
        name,
    };
};

const fetchItemsForCart = (cart: CartInterface) => async (): Promise<CartItem[]> => {
    const cartItemFetchPromises = cart.items.map(fetchCartItem);

    return Promise.all(cartItemFetchPromises);
};

const fetchPricesForCart: ReduxFetchAction<OrderResource> = cartResource => async dispatch => {
    dispatch(setIsLoading(true));

    try {
        const { tokenValue } = cartResource;
        const pricesResponse = await syliusFetch<GetOrderPricesResponse>(`/shop/orders/${tokenValue}/prices`);

        if (!isFetchResultSuccessful(pricesResponse)) {
            dispatch(setError(pricesResponse.error));
            return;
        }

        const updatedCart = transformToCart(cartResource, pricesResponse.data);
        const items = await dispatch(fetchItemsForCart(updatedCart));

        const newCart = { ...updatedCart, items };

        dispatch(setCart(newCart));
        dispatch(setOrder(newCart));
    } catch (error) {
        console.error('[fetchPricesForCart]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

const createCart: ReduxFetchAction = () => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const { customerSlice } = getState();
        const { customer } = customerSlice;

        const body = { localeCode: 'nl' };

        const createCartResponse = await syliusCustomerFetch<CreateOrderResponse>('/shop/orders', {
            customer,
            method: 'POST',
            body: JSON.stringify(body),
        });

        if (!isFetchResultSuccessful(createCartResponse)) {
            dispatch(setError(createCartResponse.error));
            return;
        }

        const { tokenValue } = createCartResponse.data;

        if (!tokenValue) {
            console.error('Token value is missing after creating a cart.');
            return;
        }

        localStorage.setItem(cartToken, JSON.stringify(tokenValue));

        const cart = transformToCart(createCartResponse.data);

        dispatch(setCart(cart));
        dispatch(setOrder(cart));
    } catch (error) {
        console.error('[createCart]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

// Recreate same cart, but with new ID, so new changes can be made
export const duplicateCart: ReduxFetchAction<string> = oldCartToken => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const { customerSlice } = getState();
        const { customer } = customerSlice;

        const duplicateCartResponse = await syliusCustomerFetch<DuplicateOrderResponse>(`/shop/orders/${oldCartToken}/duplicate`, {
            customer,
            method: 'POST',
        });

        if (!isFetchResultSuccessful(duplicateCartResponse)) {
            dispatch(setError(duplicateCartResponse.error));

            const cartDuplicationError = duplicateCartResponse.error === FetchResultSyliusErrorType.orderBelongsToOtherCustomer;

            if (cartDuplicationError) {
                await dispatch(createCart());
            }

            return;
        }

        const { token } = duplicateCartResponse.data.order;
        const cartResponse = await syliusCustomerFetch<GetOrderResponse>(`/shop/orders/${token}`, {
            customer,
        });

        if (!isFetchResultSuccessful(cartResponse)) {
            dispatch(setError(cartResponse.error));
            return;
        }

        const { tokenValue } = cartResponse.data;

        if (!tokenValue) {
            console.error('Token value of cart is missing.');
            return;
        }

        localStorage.setItem(cartToken, JSON.stringify(tokenValue));

        await dispatch(fetchPricesForCart(cartResponse.data));
    } catch (error) {
        console.error('[duplicateCart]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

const fetchCartByToken: ReduxFetchAction<GetCartByTokenConfig> = config => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    dispatch(setIsInitiallyLoading(true));
    dispatch(setError(''));
    dispatch(setHasFetched(false));

    try {
        const { customerSlice } = getState();
        const { customer } = customerSlice;

        const cartTokenValue = JSON.parse(config.storedCartToken);

        const cartResponse = await syliusCustomerFetch<GetOrderResponse>(`/shop/orders/${cartTokenValue}`, {
            customer,
        });

        if (!isFetchResultSuccessful(cartResponse)) {
            dispatch(setError(cartResponse.error));

            await dispatch(createCart());
            return;
        }

        const cartResource = cartResponse.data;
        const { tokenValue } = cartResource;

        const hasItems = cartResource.items.length > 0;

        let pricing: PricesResource | undefined;
        const { checkoutState, paymentState } = cartResource;

        if (checkoutState === CheckoutStateResource.completed && paymentState !== PaymentStateResource.paid) {
            await dispatch(duplicateCart(cartTokenValue));
            return;
        }

        if (hasItems && !config.skipPriceFetch) {
            const pricesResponse = await syliusFetch<GetOrderPricesResponse>(`/shop/orders/${tokenValue}/prices`);

            if (!isFetchResultSuccessful(pricesResponse)) {
                dispatch(setError(pricesResponse.error));

                await dispatch(createCart());
                return;
            }

            pricing = pricesResponse.data;
        }

        const cart = transformToCart(cartResource, pricing);
        const items = await dispatch(fetchItemsForCart(cart));

        const newCart = { ...cart, items };

        dispatch(setCart(newCart));
        dispatch(setOrder(newCart));
    } catch (error) {
        console.error('[fetchCartByToken]', error);
    } finally {
        dispatch(setIsLoading(false));
        dispatch(setIsInitiallyLoading(false));
    }
};

export const fetchCart: ReduxFetchAction<FetchCartConfig> = config => async dispatch => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const storedCartToken = localStorage.getItem(cartToken);

        if (!storedCartToken) {
            await dispatch(createCart());
            return;
        }

        const { skipPriceFetch } = config || {};

        await dispatch(fetchCartByToken({
            storedCartToken,
            skipPriceFetch,
        }));
    } catch (error) {
        console.error('[fetchCart]', error);
    }
};

export const addProductVariantToCart: ReduxFetchAction<string> = productVariantCode => async (dispatch, getState) => {
    dispatch(setIsSuccessfullyAdded(false));
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const { cartSlice: initialCartSlice, customerSlice } = getState();
        const { customer } = customerSlice;
        const { cart: initialCart } = initialCartSlice;

        if (!initialCart.items.length) {
            dispatch(setIsInitiallyLoading(true));
        }

        const body = {
            productVariant: productVariantCode,
            quantity: 1,
        };

        if (!initialCart.id) {
            await dispatch(createCart());
        }

        const { cartSlice } = getState();
        const { cart } = cartSlice;

        const addToCartResponse = await syliusCustomerFetch<CreateOrderItemResponse>(`/shop/orders/${cart.id}/items`, {
            customer,
            method: 'POST',
            body: JSON.stringify(body),
        });

        if (!isFetchResultSuccessful(addToCartResponse)) {
            dispatch(setError(addToCartResponse.error));

            if (addToCartResponse.type === FetchResultType.NotFoundError) {
                const storedCartToken = localStorage.getItem(cartToken);
                const cartTokenValue = JSON.parse(storedCartToken || '');

                await dispatch(duplicateCart(cartTokenValue));
            }

            return;
        }

        await dispatch(fetchPricesForCart(addToCartResponse.data));

        // TODO L5W-829: Remove once correct 'itemsTotal' is returned from addToCartResponse
        dispatch(fetchCart());

        dispatch(setIsSuccessfullyAdded(true));
    } catch (error) {
        console.error('[addProductVariantToCart]', error);
    } finally {
        dispatch(setIsLoading(false));
        dispatch(setIsInitiallyLoading(false));
    }
};

export const updateProductVariantInCart: ReduxFetchAction<ProductQuantitySubmitFormData> = formData => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const { cartItemId, quantity } = formData;
        const { cartSlice, customerSlice } = getState();
        const { customer } = customerSlice;
        const { cart } = cartSlice;

        const body = { quantity };

        const updateInCartResponse = await syliusCustomerFetch<SetOrderItemResponse>(`/shop/orders/${cart.id}/items/${cartItemId}`, {
            customer,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/merge-patch+json' },
            body: JSON.stringify(body),
        });

        if (!isFetchResultSuccessful(updateInCartResponse)) {
            dispatch(setError(updateInCartResponse.error));

            if (updateInCartResponse.type === FetchResultType.NotFoundError) {
                const storedCartToken = localStorage.getItem(cartToken);
                const cartTokenValue = JSON.parse(storedCartToken || '');

                await dispatch(duplicateCart(cartTokenValue));
            }

            return;
        }

        await dispatch(fetchPricesForCart(updateInCartResponse.data));
    } catch (error) {
        console.error('[updateProductVariantInCart]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const removeProductVariantFromCart: ReduxFetchAction<string> = cartItemId => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const { cartSlice, customerSlice } = getState();
        const { customer } = customerSlice;
        const { cart } = cartSlice;

        const removeFromCartResponse = await syliusCustomerFetch<DeleteOrderItemResponse>(`/shop/orders/${cart.id}/items/${cartItemId}`, {
            customer,
            method: 'DELETE',
        });

        if (!isFetchResultSuccessful(removeFromCartResponse)) {
            dispatch(setError(removeFromCartResponse.error));

            if (removeFromCartResponse.type === FetchResultType.NotFoundError) {
                const storedCartToken = localStorage.getItem(cartToken);
                const cartTokenValue = JSON.parse(storedCartToken || '');

                await dispatch(duplicateCart(cartTokenValue));
            }

            return;
        }

        const cartResponse = await syliusCustomerFetch<GetOrderResponse>(`/shop/orders/${cart.id}`, {
            customer,
        });

        if (!isFetchResultSuccessful(cartResponse)) {
            dispatch(setError(cartResponse.error));
            return;
        }

        await dispatch(fetchPricesForCart(cartResponse.data));

        dispatch(setIsSuccessfullyRemoved(true));
    } catch (error) {
        console.error('[removeProductVariantFromCart]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const removeCartLocally: ReduxAction = () => (dispatch, getState) => {
    const { cartSlice } = getState();
    const { cart } = cartSlice;

    if (cart.id.length) {
        dispatch(setCart(defaultCart));
        dispatch(setOrder(defaultCart));
    }

    localStorage.removeItem(cartToken);

    dispatch(fetchCart());
};

export const clearCart: ReduxFetchAction = () => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const { cartSlice, customerSlice } = getState();
        const { customer } = customerSlice;
        const { cart } = cartSlice;

        const clearCartResponse = await syliusCustomerFetch<DeleteOrderResponse>(`/shop/orders/${cart.id}`, {
            customer,
            method: 'DELETE',
        });

        if (!isFetchResultSuccessful(clearCartResponse)) {
            dispatch(setError(clearCartResponse.error));
            return;
        }

        dispatch(removeCartLocally());
    } catch (error) {
        console.error('[clearCart]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};
